/**
 * Module-level snapshot of the most recent COMPLETE billing/shipping address
 * (one with a real street line). PayPal's Smart Button strips the cart address
 * to a locality-only stub when its wallet opens and never restores it on
 * cancel, so `submitOrder` fails with "billing details are incomplete". This
 * store lets a submit-time heal put the real address back.
 *
 * Singleton (not context) so both the capturing surface (PaymentWallet) and the
 * consuming heal (Payment submit) read the same value without prop-drilling.
 */

var lastGood = null;

/**
 * Sentinel `healPaypalWipedAddress` writes into `address2` on its first,
 * perturbing write (to force a real server PUT) before writing the clean
 * address. Lives here (not utils) so the snapshot can reject it without a
 * circular import.
 */
export var PAYPAL_RESYNC_MARKER = "__paypal_resync__";
export var hasPaypalResyncMarker = function hasPaypalResyncMarker(addr) {
  var _addr$address, _addr$address2;
  return !!(addr !== null && addr !== void 0 && (_addr$address = addr.address1) !== null && _addr$address !== void 0 && _addr$address.includes(PAYPAL_RESYNC_MARKER) || addr !== null && addr !== void 0 && (_addr$address2 = addr.address2) !== null && _addr$address2 !== void 0 && _addr$address2.includes(PAYPAL_RESYNC_MARKER));
};

// Complete = has a street line AND both names. PayPal's stub wipes the street
// and the first/last name, so requiring names here stops us from memorising a
// half-stripped address as "good". An address carrying the resync marker is
// never "good": the heal's perturbing write is briefly visible in checkout
// state, and memorising it made the NEXT heal restore the marker as the
// "clean" address — which is how `__paypal_resync__` reached placed orders.
var isComplete = function isComplete(addr) {
  var _addr$address3, _addr$firstName, _addr$lastName;
  return !!(addr !== null && addr !== void 0 && (_addr$address3 = addr.address1) !== null && _addr$address3 !== void 0 && _addr$address3.trim()) && !!(addr !== null && addr !== void 0 && (_addr$firstName = addr.firstName) !== null && _addr$firstName !== void 0 && _addr$firstName.trim()) && !!(addr !== null && addr !== void 0 && (_addr$lastName = addr.lastName) !== null && _addr$lastName !== void 0 && _addr$lastName.trim()) && !hasPaypalResyncMarker(addr);
};

/**
 * Merges per-half: only overwrites a stored half when the incoming one is
 * COMPLETE. Critical because cancelling PayPal re-fires the capture while
 * billing is already a stub but shipping is still intact — a wholesale replace
 * would clobber the good billing we need for the heal.
 */
export var setLastGoodAddresses = function setLastGoodAddresses(next) {
  var _lastGood, _lastGood2;
  lastGood = {
    billing: isComplete(next.billing) ? next.billing : (_lastGood = lastGood) === null || _lastGood === void 0 ? void 0 : _lastGood.billing,
    shipping: isComplete(next.shipping) ? next.shipping : (_lastGood2 = lastGood) === null || _lastGood2 === void 0 ? void 0 : _lastGood2.shipping
  };
};
export var getLastGoodAddresses = function getLastGoodAddresses() {
  return lastGood;
};